import React, { memo, useState } from "react";
import { Breadcrumbs, Header, Workspace } from "components/Shared";
import DateRangeFilter from "./components/DateRangeFilter";
import PropertyPerformanceTable from "./components/PropertyPerformanceTable";
import SlaMetricCards from "./components/SlaMetricCards";
import TaskLifecycleFilter from "./components/TaskLifecycleFilter";
import TasksRequiringAttention from "./components/TasksRequiringAttention";
import TeamMemberFilter from "./components/TeamMemberFilter";
import { useSlaDashboard } from "./hooks/useSlaDashboard";
import { DateRangePreset, SlaActiveFilters, TaskLifecycleStatus } from "./types";

// slaStatus removed — per-section status is owned inside TasksRequiringAttention
// (api-contract D0.5 / react-frontend-tasks F4/F5).
const DEFAULT_FILTERS: SlaActiveFilters = {
  dateRangePreset: "this_financial_year",
  startDate: null,
  endDate: null,
  teamMemberIds: [],
  taskLifecycleStatus: "open", // api-contract D0.6 — default lifecycle is "open"
};

const SlaMonitorPage = () => {
  const [filters, setFilters] = useState<SlaActiveFilters>(DEFAULT_FILTERS);
  const { summary, isLoading, unauthorized } = useSlaDashboard(filters);

  const handlePresetChange = (preset: DateRangePreset) => {
    setFilters((f) => ({ ...f, dateRangePreset: preset, startDate: null, endDate: null }));
  };

  const handleCustomRange = (start: string, end: string) => {
    setFilters((f) => ({ ...f, dateRangePreset: null, startDate: start, endDate: end }));
  };

  const handleTeamMembersChange = (ids: string[]) => {
    setFilters((f) => ({ ...f, teamMemberIds: ids }));
  };

  const handleLifecycleChange = (status: TaskLifecycleStatus) => {
    setFilters((f) => ({ ...f, taskLifecycleStatus: status }));
  };

  // When the backend returns 401 (feature flag disabled), render an empty
  // shell — layout (Masthead etc.) is rendered by the route wrapper.
  if (unauthorized) return <></>;

  return (
    <React.Fragment>
      <Breadcrumbs items={[{ title: "sla-monitor" }]} />
      {/* NO "Configure SLAs" BUTTON (Sim, 22 Sep 2026). The header is the page
          title alone. `/sla-monitor/configuration` still exists and still works
          — this was its only link, so it is now reached by URL only.
          `components/ConfigureSlasButton.tsx` is left in place, unreferenced,
          rather than deleted. */}
      <Header title="SLA Monitor" stamp="sla-monitor" toolbar={[false]} />
      <Workspace id="sla-monitor" className="list">
        {/* Filters row */}
        <div className="flex items-center flex-wrap gap-2 mb-5">
          <DateRangeFilter
            preset={filters.dateRangePreset}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onPresetChange={handlePresetChange}
            onCustomRange={handleCustomRange}
          />
          <TeamMemberFilter
            selectedIds={filters.teamMemberIds}
            onChange={handleTeamMembersChange}
            filters={filters}
          />
          <TaskLifecycleFilter
            value={filters.taskLifecycleStatus}
            onChange={handleLifecycleChange}
          />
        </div>

        {/* Metric cards */}
        <SlaMetricCards
          summary={isLoading ? null : (summary?.summary ?? null)}
          lifecycle={filters.taskLifecycleStatus}
        />

        {/* Property performance table */}
        <PropertyPerformanceTable
          properties={isLoading ? [] : (summary?.properties ?? [])}
        />

        {/* Tasks requiring attention — per-section status filters managed inside */}
        <TasksRequiringAttention
          filters={filters}
        />
      </Workspace>
    </React.Fragment>
  );
};

export default memo(SlaMonitorPage);
